<template>
  <section class="hc is-top-aligned-vertical bg-gray-50">
    <div class="hc-inner">
      <slot name="breadcrumb" />
      <div class="hc-stage">
        <div class="hc-stage-content">
          <h1
            v-if="title"
            class="hc-title"
          >
            {{ title }}
          </h1>
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="description"
            class="hc-description"
            v-html="description"
          />
          <!-- eslint-enable vue/no-v-html -->

          <KfButtonContainer
            :button-container-links="links"
            :button-container-position-horizontal="btnAlignmentHorizontal"
          />

          <slot name="locator" />
        </div>
        <div class="hc-stage-image">
          <div class="hc-image-wrapper">
            <ShieldMaskFullRotated />
            <kf-image
              v-if="headerContentImage"
              :src="headerContentImage"
              :alt="imageAlt"
            />
            <div class="hc-curve hc-stage-curve">
              <kf-curve
                curve-alignment="right"
                curve-svg-color="gray"
                class="text-primary-delta"
              />
            </div>
          </div>
        </div>
      </div>
      <slot name="content" />
    </div>
    <div class="hc-curve">
      <kf-curve
        curve-alignment="left"
        curve-svg-color="white"
      />
    </div>
  </section>
</template>

<script>
import KfCurve from '@/components/base/curve/Curve.vue';
import KfImage from '@/components/base/image/Image.vue';
import KfButtonContainer from '@/components/button-container/ButtonContainer.vue';
import { trackEvent } from '@/lib/gtm/trackEvent';
import TailwindMixin from '@/mixins/TailwindMixin';

export default {
  name: 'KfHeaderContent',
  components: {
    KfButtonContainer,
    KfImage,
    KfCurve,
    ShieldMaskFullRotated: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ShieldMaskFullRotated.vue'),
  },
  mixins: [TailwindMixin],
  props: {
    imageSrcMobile: {
      type: String,
      default: '',
    },
    imageSrcLarge: {
      type: String,
      default: '',
    },
    imageAlt: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    links: {
      type: Array,
      default: null,
    },
    btnAlignmentHorizontal: {
      type: Boolean,
      default: false,
    },
    trackData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    headerContentImage() {
      let img = false;
      if (this.imageSrcMobile) {
        img = this.imageSrcMobile;
      }
      if (this.doesWindowHaveMinWidthXs && this.imageSrcLarge) {
        img = this.imageSrcLarge;
      }
      return img;
    },
  },

  mounted() {
    if (Object.keys(this.trackData)?.length > 0) {
      trackEvent(this.trackData, this.$gtm.enabled());
    }
  },
};
</script>

<style lang="postcss">
.hc {
  @apply w-full flex flex-col justify-start relative;
  min-height: 500px;

  @screen md {
    @apply pt-0;
    min-height: 670px;
  }

  // curve color
  &.has-color-gray {
    .hc-curve {
      & > svg {
        @apply text-primary-delta;
      }
    }
  }

  &-inner {
    @apply flex flex-col relative mx-auto z-20 overflow-hidden w-full;

    @screen md {
      @apply px-4 container pb-10;
      min-width: 740px;
    }
  }

  &-stage {
    /* @apply grid grid-cols-1 gap-8; */
    @apply flex;
    padding: calc(100vw - 10px) 30px 40px 30px;

    .hc-stage-content {
      @apply order-2;

      @screen md {
        width: 75%;
      }

      @screen lg {
        width: 55%;
      }

      @screen xl {
        width: 60%;
      }

      @media screen and (min-width: 1440px) {
        width: 50%;
      }
    }

    .hc-stage-image {
      @apply absolute order-1 top-0 left-0 bg-primary-alpha w-full overflow-hidden;
      height: calc(100vw - 10px);

      @screen md {
        @apply static order-none bg-transparent overflow-hidden h-auto;
        width: 25%;
      }

      @screen lg {
        @apply transform -translate-y-12;
        width: 45%;
      }

      @screen xl {
        width: 40%;
      }

      @media screen and (min-width: 1440px) {
        width: 50%;
      }
    }

    @screen md {
      @apply grid-cols-2 gap-12 p-0;

      .hc-stage-content,
      .hc-stage-image {
        @apply order-none;
      }
    }

    @screen lg {
      @apply gap-24;
    }

    .hc-stage-curve {
      @apply block;
      bottom: -1px;

      @screen md {
        @apply hidden;
      }
    }
  }

  &-image-wrapper {
    @apply absolute left-0 top-0 h-full w-full;

    @screen md {
      @apply relative;
      clip-path: url(#shape-blob-rotated);
      height: auto;
    }

    @screen lg {
      @apply relative w-full;
    }

    > img {
      @apply opacity-70 object-cover h-full w-full;
      object-position: 50% 50%;

      @screen md {
        @apply opacity-100 object-none;
      }
    }
  }

  &-title {
    @apply absolute break-words p-7 w-full left-0 top-0 flex justify-center flex-col items-center z-10 text-white text-center bg-[#00000084];
    height: 97.3vw;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    @screen md {
      @apply static p-0 w-full left-auto top-auto block text-black text-4xl text-left bg-transparent;
      height: auto;
      text-shadow: none;
    }

    @screen lg {
      @apply text-9xl;
    }
  }

  &-description {
    @apply mb-8 pt-8 whitespace-pre-wrap;

    @screen md {
      @apply pt-0;
    }

    @screen lg {
      @apply text-xl;
    }

    & > *:last-child {
      @apply mb-0;
    }
  }

  &-curve {
    @apply hidden absolute left-0 bottom-0 w-full overflow-hidden z-10;

    & > svg {
      @apply absolute top-0 left-0 w-full h-full text-white;
    }

    @screen md {
      @apply block;
    }
  }

  .locator {
    @apply mt-8;
  }
}
</style>
